import type { Ref } from 'vue'
import type { Map as LeafletMap } from 'leaflet'
import type { LMap } from '@vue-leaflet/vue-leaflet'
import type SearchResultsMapClass from '~/utils/maps/SearchResultsMap'
import { useSearchParamsStore } from '~~/stores/searchParams'
import { useSearchResultsMapStore } from '~~/stores/searchResultsMap'

const useSearchResultsMoveendSearch = (
  map: Ref<typeof LMap>,
  mapContainerRef: Ref<HTMLElement | null>,
  isMoveendActive: Ref<boolean>,
  searchResultsMap: Ref<SearchResultsMapClass | null>,
  mapSettings: { limits: boolean }
) => {
  const searchParamsStore = useSearchParamsStore()
  const mapStore = useSearchResultsMapStore()

  const skipZoomOnNextLimitsChange = ref(false)
  let safetyResetTimer: ReturnType<typeof setTimeout> | null = null
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  let skipZoomClearTimer: ReturnType<typeof setTimeout> | null = null

  const stopWatchers: Array<() => void> = []

  // Safety function: Resets spinner after 2 seconds if it gets stuck
  const safetyResetSpin = () => {
    if (safetyResetTimer) {
      clearTimeout(safetyResetTimer)
    }

    safetyResetTimer = setTimeout(() => {
      if (mapStore.isSpin) {
        const leafletMap = map.value?.leafletObject as LeafletMap | undefined
        if (leafletMap && searchResultsMap.value) {
          leafletMap.dragging.enable()
          searchResultsMap.value.showZoomControl()
          leafletMap.doubleClickZoom.enable()
          leafletMap.scrollWheelZoom.enable()

          if (mapContainerRef.value) {
            mapContainerRef.value.style.touchAction = 'auto'
          }
        }

        // Reset spinner state - leave moveMapSearch unchanged so subsequent drags work
        mapStore.isSpin = false
      }
    }, 2000)
  }

  const registerHandlers = () => {
    const leafletMap = map.value?.leafletObject as LeafletMap

    // MOVEEND EVENT: Handles both drag and zoom
    // Both actions trigger search with 1-second debounce
    leafletMap.on('moveend', () => {
      if (!isMoveendActive.value) {
        return
      }

      // Check if this moveend was triggered by programmatic zoom
      // This prevents infinite loops when zoomMap() is called after backend returns results
      if (skipZoomOnNextLimitsChange.value) {
        skipZoomOnNextLimitsChange.value = false
        return
      }

      if (mapStore.moveMapSearch) {
        if (mapSettings.limits) {
          // Cancel previous debounce timer (handles combined drag+zoom and rapid moves)
          clearTimeout(timeoutId ?? undefined)
          const preservePage = mapStore.preservePageOnNextLimitsChange
          mapStore.preservePageOnNextLimitsChange = false
          timeoutId = setTimeout(() => {
            const bounds = leafletMap.getBounds()
            const northEast = bounds.getNorthEast()
            const southWest = bounds.getSouthWest()
            const coordinates = northEast.lat + ';' + northEast.lng + ';' + southWest.lat + ';' + southWest.lng

            // If limits are unchanged, return early without modifying any state
            // This prevents map blocking when user zooms to same coordinates
            if (coordinates === searchParamsStore.limits) {
              return
            }

            leafletMap.dragging.disable()

            mapStore.isSpin = true
            safetyResetSpin() // Start safety timeout
            mapStore.moveMapSearch = true

            // CRITICAL: Set flag BEFORE setting limits to prevent watcher loop
            skipZoomOnNextLimitsChange.value = true
            // Safety: auto-clear skipZoom after 500ms if limits watcher never fires
            if (skipZoomClearTimer) clearTimeout(skipZoomClearTimer)
            skipZoomClearTimer = setTimeout(() => {
              if (skipZoomOnNextLimitsChange.value) {
                skipZoomOnNextLimitsChange.value = false
              }
              skipZoomClearTimer = null
            }, 500)
            if (preservePage) {
              searchParamsStore.$patch({
                force_page_change: true,
                limits: coordinates
              })
            } else {
              searchParamsStore.limits = coordinates
            }
            mapStore.moveMapIteration++
          }, 1000) // 1 second debounce for both drag and zoom
        }
      } else {
        mapStore.moveMapSearch = true
      }
    })

    // isSpin watcher is registered at setup level (not here) to ensure
    // it stays alive across component cycles (Vue auto-cleans watchers
    // created outside setup() when the component unmounts).
  }

  // ── Setup-level isSpin watcher ────────────────────────────────────────────
  // Must be registered HERE (composable body = called during setup()) so Vue's
  // effect scope tracks it for the full component lifetime.
  // If registered inside registerHandlers() (@ready callback), Vue may stop it
  // at component unmount even though the manual stopWatchers cleanup also exists.
  stopWatchers.push(watch(() => mapStore.isSpin, () => {
    const leafletMapInner = map.value?.leafletObject as LeafletMap | undefined
    if (!searchResultsMap.value || !leafletMapInner) { return }
    if (mapStore.isSpin) {
      leafletMapInner.dragging.disable()
      leafletMapInner.doubleClickZoom.disable()
      searchResultsMap.value.hideZoomControl()
      leafletMapInner.scrollWheelZoom.disable()
      if (mapContainerRef.value) {
        mapContainerRef.value.style.touchAction = 'none'
      }
    } else {
      // Clear safety timer when spinner is disabled normally
      if (safetyResetTimer) {
        clearTimeout(safetyResetTimer)
        safetyResetTimer = null
      }

      leafletMapInner.dragging.enable()
      searchResultsMap.value.showZoomControl()
      leafletMapInner.doubleClickZoom.enable()
      leafletMapInner.scrollWheelZoom.enable()
      if (mapContainerRef.value) {
        mapContainerRef.value.style.touchAction = 'auto'
      }
    }
  }))

  const cleanupTimers = () => {
    if (safetyResetTimer) {
      clearTimeout(safetyResetTimer)
      safetyResetTimer = null
    }

    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }

    if (skipZoomClearTimer) {
      clearTimeout(skipZoomClearTimer)
      skipZoomClearTimer = null
    }

    skipZoomOnNextLimitsChange.value = false

    stopWatchers.forEach(stop => stop())
    stopWatchers.length = 0
  }

  return {
    registerHandlers,
    skipZoomOnNextLimitsChange,
    cleanupTimers
  }
}

export default useSearchResultsMoveendSearch
